import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import Axios from 'axios';

import ProgressIndicator from '../Common/ProgressIndicator';
import ErrorIndicator from '../Common/ErrorIndicator';
import SelectFieldGroup from '../Common/SelectFieldGroup';

const DataTable = forwardRef((props, ref) => {
    const [response, setResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");

    const [selectedIds, setSelectedIds] = useState([]);

    // Kívülről elérhető metódus a ref segítségével
    useImperativeHandle(ref, () => ({
        triggerFetchData
    }));

    const triggerFetchData = () => {
        setIsLoading(true);
        Axios.get(`${props.url}?page=${page}&pageSize=${pageSize}${sortField ? `&sortField=${sortField}&sortDirection=${sortDirection}` : ''}`)
            .then(res => {
                console.log(res);
                setResponse(res);
                setIsLoading(false);
            }, err => {
                setIsLoading(false);
                setError(true);
            });
    };

    useEffect(() => {
        triggerFetchData();
    }, [page, pageSize, props.url, sortField, sortDirection]);

    const changeSelection = (id) => {
        const newSelected = selectedIds.includes(id)
            ? selectedIds.filter(i => i !== id)
            : [...selectedIds, id];

        setSelectedIds(newSelected);

        if (props.onSelectionChange) {
            props.onSelectionChange(newSelected);
        }
    };

    const paginationArray = response
        ? [...Array(response.pages).keys()].slice(Math.max(page - 5, 0), page + 5)
        : [];

    if (isLoading) return <ProgressIndicator />;
    if (error) return <ErrorIndicator text={props.errorMessage} />;
    if (!response || response.data.length === 0) return <p><em>Nincs megjeleníthető adat.</em></p>;

    return (
        <>
            <table className="table table-hover">
                <thead>
                <tr className="bg-transparent fw-bold">
                    <td>Kijelölés</td>
                    {props.columns.map((col, index) => {
                        const sortableAccessors = ["firstLoginDate", "lastLoginDate"];
                        const isSortable = sortableAccessors.includes(col.accessor);

                        return (
                            <td
                                key={index}
                                onClick={() => {
                                    if (!isSortable) return;
                                    if (sortField === col.accessor) {
                                        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                                    } else {
                                        setSortField(col.accessor);
                                        setSortDirection("asc");
                                    }
                                }}
                                style={{ cursor: isSortable ? 'pointer' : 'default' }}
                            >
                                {col.header}
                                {isSortable && sortField === col.accessor
                                    ? (sortDirection === "asc" ? " 🔼" : " 🔽")
                                    : ""}
                            </td>
                        );
                    })}
                </tr>
                </thead>
                <tbody>
                {response.data.map(row =>
                    <tr className="bg-transparent" key={row.id}>
                        <td>
                            <input
                                type="checkbox"
                                checked={selectedIds.includes(row.id)}
                                onChange={() => changeSelection(row.id)}
                            />
                        </td>
                        {props.columns.map((col, index) => (
                            <td key={index}>
                                {col.cell && col.cell(col.accessor ? row[col.accessor] : row)}
                                {!col.cell && row[col.accessor]}
                            </td>
                        ))}
                    </tr>
                )}
                </tbody>
            </table>

            <div className="d-flex flex-row justify-content-center gap-2">
                <button type="button" className="btn btn-primary" onClick={() => setPage(0)}>Első</button>
                {
                    paginationArray.map(i => (
                        <button type="button"
                                className={`btn btn-${page === i ? 'secondary' : 'primary'}`}
                                key={i}
                                onClick={() => setPage(i)}>
                            {i + 1}
                        </button>
                    ))
                }
                <button type="button" className="btn btn-primary" onClick={() => setPage(response.pages - 1)}>Utolsó</button>
            </div>

            <div className="d-flex flex-row gap-3">
                <SelectFieldGroup
                    field="pageSize"
                    onChange={e => setPageSize(parseInt(e.target.value))}
                    label="Oldalméret"
                    value={pageSize}
                >
                    {[10, 20, 50, 100].map(i => <option value={i} key={i}>{i}</option>)}
                </SelectFieldGroup>
            </div>
        </>
    );
});

export default DataTable;
